import { useFormikContext } from 'formik'
import { useEffect, useRef } from 'react'
import useToastr from '../../../../../hooks/useToastr'
import { MailModel } from '../../../../../shared/models/Mail.model'
import { UserUploadedFileModel } from '../../../../../shared/models/service-models/UserUploadedFile.model'
import UserUploadedFileService from '../../../../../services/UserUploadedFileService'

export const ACCEPTED_EXTENSIONS = [
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.ppt',
  '.pptx',
  '.pdf',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.bmp',
  '.zip',
  '.msg',
]

export const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.join(',')

export const getFileExtension = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf('.')
  if (lastDotIndex < 0) return ''
  return fileName.substring(lastDotIndex).toLowerCase()
}

type Props = {
  isValidSize: (
    newAttachments: Pick<UserUploadedFileModel, 'originalFileName' | 'fileSize'>[]
  ) => boolean
  maxTotalSizeMB: number
}

export const useEmailAttachmentsAppender = ({ isValidSize, maxTotalSizeMB }: Props) => {
  const { showError } = useToastr()
  const { values, setFieldValue } = useFormikContext<MailModel>()
  const attachmentsRef = useRef(values.attachments ?? [])

  useEffect(() => {
    attachmentsRef.current = values.attachments ?? []
  }, [values.attachments])

  const appendFiles = async (incomingFiles: File[]) => {
    if (!incomingFiles.length) return

    const currentAttachments = attachmentsRef.current

    incomingFiles = incomingFiles.filter(
      file =>
        !currentAttachments.some(
          att => att.originalFileName === file.name && att.fileSize === file.size
        )
    )

    if (!incomingFiles.length) {
      return
    }

    const hasInvalidFile = incomingFiles.some(
      file => !ACCEPTED_EXTENSIONS.includes(getFileExtension(file.name))
    )
    if (hasInvalidFile) {
      showError(
        'Unsupported File Type',
        'The file type you are attempting to attach is not supported. Please attach a file with one of the accepted formats.'
      )
      return
    }

    const uniqueAttachments = [
      ...new Map(
        [
          ...currentAttachments,
          ...incomingFiles.map(ic => ({ originalFileName: ic.name, fileSize: ic.size })),
        ].map(file => [`${file.originalFileName}-${file.fileSize}`, file])
      ).values(),
    ]

    if (!isValidSize(uniqueAttachments)) {
      showError(
        'File Size Exceeded',
        `The total attachment size will exceed the maximum allowed limit of ${maxTotalSizeMB} MB. Please keep attachments within the allowed limit to proceed.`
      )
      return
    }

    const uploadPromises = incomingFiles.map(file => UserUploadedFileService.uploadFile(file))
    const uploadResults = await Promise.all(uploadPromises)

    if (uploadResults.some(result => !result?.isSuccess)) {
      showError('File Upload Failed', `One or more files failed to upload. Please try again.`)
      return
    }

    const updatedAttachments = [...currentAttachments, ...uploadResults.map(result => result.data)]
    attachmentsRef.current = updatedAttachments
    setFieldValue('attachments', updatedAttachments)
  }

  return { appendFiles }
}
